/**
 * A chart can read its rows from a CSV the assistant exported, so a large
 * dataset is summarized in the browser instead of passing through the model.
 */ /** Parses CSV (RFC 4180: quoted fields, doubled quotes, CRLF) into records keyed by header. */ export function parseCsv(raw, maxRows = 200000) {
    // Exports start with a byte-order mark so Excel reads UTF-8; it is not part of the first header.
    const text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
    const rows = [];
    let field = '';
    let row = [];
    let quoted = false;
    for(let i = 0; i < text.length; i++){
        const c = text[i];
        if (quoted) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else quoted = false;
            } else field += c;
            continue;
        }
        if (c === '"' && field === '') quoted = true;
        else if (c === ',') {
            row.push(field);
            field = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n') i++;
            row.push(field);
            field = '';
            if (row.some((v)=>v !== '')) rows.push(row);
            row = [];
            if (rows.length > maxRows) break;
        } else field += c;
    }
    if (field !== '' || row.length) {
        row.push(field);
        if (row.some((v)=>v !== '')) rows.push(row);
    }
    const [header, ...body] = rows;
    if (!header) return [];
    const keys = header.map((h)=>h.trim());
    return body.map((r)=>Object.fromEntries(keys.map((k, i)=>[
                k,
                r[i] ?? ''
            ])));
}
/** A number from a cell ("$1,234.50", "12%", "(300)" as -300); NaN when it is not one. */ export function cellNumber(v) {
    if (v === undefined) return Number.NaN;
    let s = v.trim().replace(/[$,%\s]/g, '');
    const negative = /^\(.*\)$/.test(s);
    if (negative) s = s.slice(1, -1);
    if (s === '') return Number.NaN;
    const n = Number(s);
    return negative ? -n : n;
}
/**
 * Groups records by x (and series), combining y. `top` keeps the largest N
 * groups by total; `sortByX` orders groups by x instead (a time axis).
 */ export function aggregate(records, opts) {
    const groups = new Map();
    const seriesSeen = new Set();
    let skipped = 0;
    for (const r of records){
        const x = (r[opts.x] ?? '').trim();
        if (!x) continue;
        const s = opts.series ? (r[opts.series] ?? '').trim() || '—' : '';
        let value = 1;
        if (opts.agg !== 'count') {
            value = cellNumber(r[opts.y ?? '']);
            if (!Number.isFinite(value)) {
                skipped++;
                continue;
            }
        }
        seriesSeen.add(s);
        const g = groups.get(x) ?? new Map();
        const cell = g.get(s) ?? {
            sum: 0,
            n: 0
        };
        cell.sum += value;
        cell.n += 1;
        g.set(s, cell);
        groups.set(x, g);
    }
    let rows = [
        ...groups.entries()
    ].map(([x, g])=>{
        const values = {};
        for (const [s, c] of g)values[s] = opts.agg === 'avg' ? c.sum / c.n : c.sum;
        return {
            x,
            values,
            total: Object.values(values).reduce((a, v)=>a + v, 0)
        };
    });
    if (opts.sortByX) {
        rows.sort((a, b)=>a.x.localeCompare(b.x, undefined, {
                numeric: true
            }));
    } else {
        rows.sort((a, b)=>b.total - a.total);
        if (opts.top && opts.top > 0) rows = rows.slice(0, opts.top);
    }
    return {
        rows,
        series: [
            ...seriesSeen
        ],
        read: records.length,
        skipped
    };
}
const norm = (s)=>s.toLowerCase().replace(/[^a-z0-9]/g, '');
/**
 * The file's header a chart's column names: the exact header, else the same
 * words ignoring case and punctuation, else the one header that starts with
 * them ("obligated" -> "Obligated in FY ($)"). Undefined when none is sure.
 */ export function resolveColumn(headers, want) {
    if (headers.includes(want)) return want;
    const w = norm(want);
    if (!w) return undefined;
    const same = headers.filter((h)=>norm(h) === w);
    if (same.length === 1) return same[0];
    const starts = headers.filter((h)=>norm(h).startsWith(w));
    return starts.length === 1 ? starts[0] : undefined;
}
